import { Injectable } from "@angular/core";
import { AccountAppDeviceHandlerService } from "../../handlers/account-app-device-handler.service";
import { SavedSearchesHandlerService } from "../../handlers/saved-searches-handler.service";
import { SessionService } from "../../session.service";

export interface CustomerRosterSearchPayload {
  schema_version?: 1;
  context: {
    account_id: number;
    account_app_id: number;
    facility_ids?: number[];
    include_null_facilities?: boolean;
    mode?: {
      registration?: "both" | "registered" | "anonymous";
      devices?: "latest_per_user" | "all_per_user";
      include_null_facilities?: boolean;
    };
  };
  query: any;
  metadata?: { name?: string; description?: string; tags?: string[] };
}

@Injectable({ providedIn: "root" })
export class CustomerSearchService {
  constructor(
    private accountAppDeviceHandler: AccountAppDeviceHandlerService,
    private savedSearchesHandler: SavedSearchesHandlerService,
    private sessionService: SessionService,
  ) {}

  buildBasePayload(partial?: Partial<CustomerRosterSearchPayload>): CustomerRosterSearchPayload {
    const account_id = this.sessionService.currentAccountValue!.accountID;
    const account_app_id = this.sessionService.currentAppValue!.id;
    const facility_ids = this.sessionService.facilitiesValue?.map((f) => f.id) || [];

    const base: CustomerRosterSearchPayload = {
      schema_version: 1,
      context: {
        account_id,
        account_app_id,
        facility_ids,
        include_null_facilities: true,
      },
      query: {
        pagination: { page: 0, per_page: 50 },
      },
    };

    // Merge partial at the end so callers can override defaults explicitly
    if (partial?.context) {
      base.context = { ...base.context, ...partial.context } as any;
      if ((partial.context as any).mode) {
        base.context.mode = { ...(base.context.mode || {}), ...(partial.context as any).mode };
      }
    }
    if (partial?.query) {
      base.query = { ...base.query, ...partial.query } as any;
    }
    if (partial?.metadata) {
      (base as any).metadata = partial.metadata;
    }

    return base;
  }

  private normalizePayload(payload: CustomerRosterSearchPayload): CustomerRosterSearchPayload {
    const context: any = { ...(payload?.context || {}) };

    // Backward compatibility for older persisted payloads.
    if (
      typeof context.include_null_facilities === "undefined" &&
      typeof context.allow_null_facility !== "undefined"
    ) {
      context.include_null_facilities = !!context.allow_null_facility;
    }
    delete context.allow_null_facility;

    return {
      ...payload,
      context,
    };
  }

  runSearch(payload: CustomerRosterSearchPayload) {
    const normalized = this.normalizePayload(payload);
    const account_id = normalized.context.account_id;
    const account_app_id = normalized.context.account_app_id;
    return this.accountAppDeviceHandler.search(account_id, account_app_id, normalized);
  }

  listSavedSearches(type: string = "customer_roster") {
    const account_id = this.sessionService.currentAccountValue!.accountID;
    const account_app_id = this.sessionService.currentAppValue!.id;
    return this.savedSearchesHandler.list(account_id, account_app_id, type);
  }

  createSavedSearch(body: any) {
    const account_id = this.sessionService.currentAccountValue!.accountID;
    const account_app_id = this.sessionService.currentAppValue!.id;
    return this.savedSearchesHandler.create(account_id, account_app_id, body);
  }

  updateSavedSearch(id: number, body: any) {
    const account_id = this.sessionService.currentAccountValue!.accountID;
    const account_app_id = this.sessionService.currentAppValue!.id;
    return this.savedSearchesHandler.update(account_id, account_app_id, id, body);
  }

  deleteSavedSearch(id: number) {
    const account_id = this.sessionService.currentAccountValue!.accountID;
    const account_app_id = this.sessionService.currentAppValue!.id;
    return this.savedSearchesHandler.delete(account_id, account_app_id, id);
  }

  runSavedSearch(id: number) {
    const account_id = this.sessionService.currentAccountValue!.accountID;
    const account_app_id = this.sessionService.currentAppValue!.id;
    return this.savedSearchesHandler.run(account_id, account_app_id, id);
  }

  runSavedSearchWithPagination(id: number, page: number, per_page: number) {
    const account_id = this.sessionService.currentAccountValue!.accountID;
    const account_app_id = this.sessionService.currentAppValue!.id;
    return this.savedSearchesHandler.run(account_id, account_app_id, id, {
      pagination: { page, per_page },
    });
  }
}


